import { AfterViewInit, ChangeDetectorRef, Component, Injectable, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { select, Store } from '@ngrx/store';
import { Observable, of, Subject, Subscription, BehaviorSubject, combineLatest } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AuthService, currentUser, User } from '../../../../core/auth';
import { ModuleService } from '../../../../core/plant-configuration/_services/module.service';
import { AppState } from '../../../../core/reducers';
import { LayoutUtilsService, QueryParamsModel, QueryResultsModel } from '../../../../core/_base/crud';
import { SubheaderService } from '../../../../core/_base/layout';

import { SelectionModel } from '@angular/cdk/collections';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { FormBuilder, Validators } from '@angular/forms';
import { OperatorService } from '../../../../core/plant-configuration/_services/operator.service';
import { CompanyService } from '../../../../core/auth/_services/company.service';
import { GroupService } from '../../../../core/auth/_services/group.service';

/**
 * Node for to-do item
 */
export class TodoItemNode {
  children: TodoItemNode[];
  item: string;
}

/** Flat to-do item node with expandable and level information */
export class TodoItemFlatNode {
  item: string;
  level: number;
  expandable: boolean;
}

/**
 * The Json object for to-do list data.
 */
const TREE_DATA = {
  Groceries: {
    'Almond Meal flour': null,
    'Organic eggs': null,
    'Protein Powder': null,
    Fruits: {
      Apple: null,
      Berries: ['Blueberry', 'Raspberry'],
      Orange: null
    }
  },
  Reminders: [
    'Cook dinner',
    'Read the Material Design spec',
    'Upgrade Application to Angular'
  ]
};

/**
 * Checklist database, it can build a tree structured Json object.
 * Each node in Json object represents a to-do item or a category.
 * If a node is a category, it has children items and new items can be added under the category.
 */
@Injectable()
export class ChecklistDatabase {
  dataChange = new BehaviorSubject<TodoItemNode[]>([]);

  get data(): TodoItemNode[] { return this.dataChange.value; }

  constructor() {
    this.initialize(TREE_DATA);
  }

  initialize(treeData) {
    // Build the tree nodes from Json object. The result is a list of `TodoItemNode` with nested
    //     file node as children.
    const data = this.buildFileTree(treeData, 0);

    // Notify the change.
    this.dataChange.next(data);
  }

  /**
   * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
   * The return value is the list of `TodoItemNode`.
   */
  buildFileTree(obj: { [key: string]: any }, level: number): TodoItemNode[] {
    return Object.keys(obj).reduce<TodoItemNode[]>((accumulator, key) => {
      const value = obj[key];
      const node = new TodoItemNode();
      node.item = key;

      if (value != null) {
        if (typeof value === 'object') {
          node.children = this.buildFileTree(value, level + 1);
        } else {
          node.item = value;
        }
      }

      return accumulator.concat(node);
    }, []);
  }

  /** Add an item to to-do list */
  insertItem(parent: TodoItemNode, name: string) {
    if (parent.children) {
      parent.children.push({ item: name } as TodoItemNode);
      this.dataChange.next(this.data);
    }
  }

  updateItem(node: TodoItemNode, name: string) {
    node.item = name;
    this.dataChange.next(this.data);
  }
}

/**
 * @title Tree with checkboxes
 */
@Component({
  selector: 'tree-checklist-example',
  templateUrl: 'tree-checklist-example.html',
  styleUrls: [],
  providers: [ChecklistDatabase]
})
export class TreeChecklistExample {
  /** Map from flat node to nested node. This helps us finding the nested node to be modified */
  flatNodeMap = new Map<TodoItemFlatNode, TodoItemNode>();

  /** Map from nested node to flattened node. This helps us to keep the same object for selection */
  nestedNodeMap = new Map<TodoItemNode, TodoItemFlatNode>();

  /** A selected parent node to be inserted */
  selectedParent: TodoItemFlatNode | null = null;

  /** The new item's name */
  newItemName = '';

  treeControl: FlatTreeControl<TodoItemFlatNode>;

  treeFlattener: MatTreeFlattener<TodoItemNode, TodoItemFlatNode>;

  dataSource: MatTreeFlatDataSource<TodoItemNode, TodoItemFlatNode>;

  /** The selection for checklist */
  checklistSelection = new SelectionModel<TodoItemFlatNode>(true /* multiple */);

  constructor(private _database: ChecklistDatabase) {
    this.treeFlattener = new MatTreeFlattener(this.transformer, this.getLevel,
      this.isExpandable, this.getChildren);
    this.treeControl = new FlatTreeControl<TodoItemFlatNode>(this.getLevel, this.isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  }

  init(tree) {
    this._database.initialize(tree);
    this._database.dataChange.subscribe(data => {
      this.dataSource.data = data;
      console.dir("tree inited: ");
      console.dir(this.treeFlattener);
      console.dir(this.checklistSelection);
      console.dir(this.dataSource);
      console.dir(this.treeControl);
      console.dir(this.nestedNodeMap);
      this.treeControl.dataNodes.forEach(node=>{
        this.checklistSelection.select(node);
      })
    });
  }

  getLevel = (node: TodoItemFlatNode) => node.level;

  isExpandable = (node: TodoItemFlatNode) => node.expandable;

  getChildren = (node: TodoItemNode): TodoItemNode[] => node.children;

  hasChild = (_: number, _nodeData: TodoItemFlatNode) => _nodeData.expandable;

  hasNoContent = (_: number, _nodeData: TodoItemFlatNode) => _nodeData.item === '';

  /**
   * Transformer to convert nested node to flat node. Record the nodes in maps for later use.
   */
  transformer = (node: TodoItemNode, level: number) => {
    const existingNode = this.nestedNodeMap.get(node);
    const flatNode = existingNode && existingNode.item === node.item
      ? existingNode
      : new TodoItemFlatNode();
    flatNode.item = node.item;
    flatNode.level = level;
    // flatNode.expandable = !!node.children?.length;
    flatNode.expandable = (node.children && node.children.length) ? true : false;
    this.flatNodeMap.set(flatNode, node);
    this.nestedNodeMap.set(node, flatNode);
    return flatNode;
  }

  /** Whether all the descendants of the node are selected. */
  descendantsAllSelected(node: TodoItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.length > 0 && descendants.every(child => {
      return this.checklistSelection.isSelected(child);
    });
    return descAllSelected;
  }

  /** Whether part of the descendants are selected */
  descendantsPartiallySelected(node: TodoItemFlatNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    const result = descendants.some(child => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  /** Toggle the to-do item selection. Select/deselect all the descendants node */
  todoItemSelectionToggle(node: TodoItemFlatNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node)
    //? this.checklistSelection.select(...descendants)
    //: this.checklistSelection.deselect(...descendants);

    // Force update for the parent
    descendants.forEach(child => this.checklistSelection.isSelected(child));
    this.checkAllParentsSelection(node);
  }

  /** Toggle a leaf to-do item selection. Check all the parents to see if they changed */
  todoLeafItemSelectionToggle(node: TodoItemFlatNode): void {
    this.checklistSelection.toggle(node);
    this.checkAllParentsSelection(node);
  }

  /* Checks all the parents when a leaf node is selected/unselected */
  checkAllParentsSelection(node: TodoItemFlatNode): void {
    let parent: TodoItemFlatNode | null = this.getParentNode(node);
    while (parent) {
      this.checkRootNodeSelection(parent);
      parent = this.getParentNode(parent);
    }
  }

  /** Check root node checked state and change it accordingly */
  checkRootNodeSelection(node: TodoItemFlatNode): void {
    const nodeSelected = this.checklistSelection.isSelected(node);
    const descendants = this.treeControl.getDescendants(node);
    const descAllSelected = descendants.length > 0 && descendants.every(child => {
      return this.checklistSelection.isSelected(child);
    });
    if (nodeSelected && !descAllSelected) {
      this.checklistSelection.deselect(node);
    } else if (!nodeSelected && descAllSelected) {
      this.checklistSelection.select(node);
    }
  }

  /* Get the parent node of a node */
  getParentNode(node: TodoItemFlatNode): TodoItemFlatNode | null {
    const currentLevel = this.getLevel(node);

    if (currentLevel < 1) {
      return null;
    }

    const startIndex = this.treeControl.dataNodes.indexOf(node) - 1;

    for (let i = startIndex; i >= 0; i--) {
      const currentNode = this.treeControl.dataNodes[i];

      if (this.getLevel(currentNode) < currentLevel) {
        return currentNode;
      }
    }
    return null;
  }

  /** Select the category so we can insert the new item. */
  addNewItem(node: TodoItemFlatNode) {
    const parentNode = this.flatNodeMap.get(node);
    this._database.insertItem(parentNode!, '');
    this.treeControl.expand(node);
  }

  /** Save the node to database */
  saveNode(node: TodoItemFlatNode, itemValue: string) {
    const nestedNode = this.flatNodeMap.get(node);
    this._database.updateItem(nestedNode!, itemValue);
  }
}

@Component({
  selector: 'kt-module-list',
  templateUrl: './module-list.component.html',
  styleUrls: ['./module-list.component.scss'],
  providers: [ChecklistDatabase]
})
export class ModuleListComponent extends TreeChecklistExample implements OnInit, OnDestroy, AfterViewInit {

  inited = false;
  private subscriptions: Subscription[] = [];
  moduleList: any[] = [];

  editing = false;

  itemObs = new Subject<any[]>();
  editObs = new Subject<number>();
  addObs = new Subject<number>();
  private itemFormOptions = {
    company_id: ["", Validators.required],
    group_id: ["", Validators.required]
  };
  itemForm = this.formBuilder.group(this.itemFormOptions);
  stepList: any[] = [
    {
      icon: "flaticon2-user-outline-symbol", title: "Seleccionar empresas", subTitle: "Seleccione empresas habilitadas", form: [
        [
          { name: "company_id", listTitle: 'Empresas seleccionadas', label: "Empresas", type: "multiple", optionList: [], selected: []},
        ]
      ]
    },
    {
      icon: "flaticon2-user-outline-symbol", title: "Seleccionar grupos", subTitle: "Seleccione grupos de usuarios habilitados", form: [
        [
          { name: "group_id", listTitle: 'Grupos seleccionados', label: "Grupos", type: "multiple", optionList: [], selected: []},
        ]
      ]
    },
  ];

  data: any = {};
  partnerList: any[];
  userList: any[];
  uid: number = -1;
  moduleTable: any[];

  constructor(
    private moduleService: ModuleService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private layoutUtilsService: LayoutUtilsService,
    private subheaderService: SubheaderService,
    private cdr: ChangeDetectorRef,
    private store: Store<AppState>,
    private checklistDatabase: ChecklistDatabase,
    private operatorService: OperatorService,
    private companyService: CompanyService,
    private groupService: GroupService,
    private authService: AuthService,
    private formBuilder: FormBuilder
  ) {
    super(checklistDatabase);
  }
  // {
  // 	path: 'modules',
  // 	component: ModuleListComponent,
  // 	canActivate: [RoleGuard],
  // 	data: {
  // 		groups: [4]
  // 	}
  // },

  ngOnInit() {
    this.uid = this.authService.getUID();

    this.subscriptions.push(
      of(null).pipe(
        switchMap(()=>combineLatest([
          this.companyService.getAll(),
          this.groupService.getAll()
        ]))
      ).subscribe(([companyList, groupList])=>{
        this.stepList[0].form[0][0].optionList = companyList.map(company=>{return{text:company.name, value:company.id}});
        this.stepList[1].form[0][0].optionList = groupList.map(group=>{return{text:group.name, value:group.id}});
        this.data.companyList = companyList;
        this.data.groupList = groupList;
        this.cdr.detectChanges();
      })
    )
    
    this.subscriptions.push(
      of(null).pipe(
        switchMap(() => {
          const getAllObs = this.moduleService.getAllInExistence();
          return combineLatest([
            getAllObs,
            getAllObs.pipe(map(this.moduleService.mapToModuleList)),
            getAllObs.pipe(map(this.moduleService.mapToModuleTree)),
          ])
        })
      ).subscribe(([moduleTable, moduleList, moduleTree]) => {
        this.moduleTable = moduleTable;
        this.moduleList = moduleList;
        console.log("moduleTable: ");console.dir(moduleTable);
        console.log("moduleList: "); console.dir(this.moduleList);
        console.log("moduleTree: "); console.dir(moduleTree);
        this.init(moduleTree);
        this.inited = true;
        this.cdr.detectChanges();
      })
    );

    // this.subheaderService.setTitle('Configuración de módulos');
		// this.subheaderService.setBreadcrumbs([
		// 	{ title: 'Configuración ControlIR4', page: `/controlir4-config/modules` },
		// 	{ title: 'Módulos', page: `/controlir4-config/modules` },
		// 	// { title: 'part 2', page: `/ecommerce/products` },
		// 	// { title: 'part 3', page: `/ecommerce/products/edit`, queryParams: { id: 1 } }
		// ]);
    // // this.subheaderService.updateBreadcrumbs();
    // this.subheaderService.disabled$.next(false);
  }
  /**
   * On Destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  editSelected(){
    console.log("selected: ");console.dir(this.checklistSelection.selected);
    this.editing = true;
  }

  ngAfterViewInit() {
  }

  onItemLoaded(item) {

    console.log("on item load:");
  }

  getItem(itemId: number) {
    this.subscriptions.push(
      of(null).pipe(
        switchMap(() => this.operatorService.getById(itemId))
      ).subscribe(item => {
        this.onItemLoaded(item);
        this.itemObs.next(item);
      })
    )
  }

  addItem(item: any) {
    item.create_uid = this.uid>0?this.uid:'null';
    item.create_date = "now()"; 
    console.dir(item);
    // console.log("checked: ");console.dir(this.checklistSelection.selected);
    // console.log("db check: ");console.dir(this.checklistDatabase);
    // console.log("flat node map"); console.dir(this.flatNodeMap);
    // console.log("tree control"); console.dir(this.treeControl);
    const selected = this.checklistSelection.selected;
    var moduleTable = [];
    selected.forEach(node=>{
      var moduleTableTmp = [];
      if(node.level==0){
        moduleTableTmp = this.moduleTable.filter(module_=>module_.name==node.item);
      }else if(node.level==1){
        moduleTableTmp = this.moduleTable.filter(module_=>module_.sub_module_name == node.item);
        
      }else if(node.level==2){
        moduleTableTmp = this.moduleTable.filter(module_=>module_.sub_sub_module_name == node.item && 
          module_.sub_module_name == this.getParentNode(node).item);
      }
      moduleTable = moduleTable.concat(moduleTableTmp);
    });
    console.log("module table selected: ");console.dir(moduleTable);
    item['moduleTable'] = moduleTable;
    // console.dir(this.getParentNode(this.checklistSelection.selected[0]));

    // item.is_installation_contact = 'true';
    // item.active = 'true';
    this.subscriptions.push(
      of(null).pipe(
        switchMap(() => this.moduleService.add(item))
      ).subscribe(newId => {
        this.addObs.next(newId);
      })
    )
  }

  editItem(item: any) {
    item.write_uid = this.uid>0?this.uid:'null';
    item.write_date = "now()";
    console.dir(item);
    // item.is_installation_contact = 'true';
    // this.subscriptions.push(
    //   of(null).pipe(
    //     switchMap(() => this.operatorService.edit(item))
    //   ).subscribe(status => {
    //     if (status == "success") this.editObs.next(item.id);
    //     else this.editObs.next(-1);
    //   })
    // )
  }

  goBack(event){
    this.editing = false;
  }
}